import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Typography, TextField, Link } from '@mui/material';

import { viewEstimate } from '../../../modules/projectModule/projectEstimate';
import { setProject } from '../../../modules/projectModule/project';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as chanceApi from '../../../lib/api/chance';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import CostCalcLockContainer from '../costcalc/CostCalcLockContainer';
import AskDialog from '../../../components/common/dialog/AskDialog';

import ForecastBaseInfoForm from '../../../components/common/base/ForecastBaseInfoForm';
import { getInitialFormValues } from '../../../components/common/base/ForecastBaseInfoInit';
import { useFormik } from 'formik';
import { validationForecast } from '../../../components/common/util/ForecastValid';

const ProjectDetailInfoContainer = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { project, estimate, user } = useSelector(({auth, projectModule }) => ({
    user: auth.user,
    project: projectModule.project.project,
    estimate: projectModule.projectEstimate.estimate,
  }));

  // 1. 잠금 상태 관리 (기본값: 잠금/읽기전용)
  const [isLocked, setIsLocked] = useState(true);  
  const [updateNot, setUpdateNot] = useState(true);  
    
  // 2. 클릭 핸들러
  const handleLockClick = (e) => {
    e.stopPropagation(); 
    setIsLocked(!isLocked);
  };

  useEffect(() => {
    setIsLocked(isLocked);
    if (project) {
      dispatch(viewEstimate(project.prjUuid));
    }
  }, [dispatch, project]);

  //원가산출 확정/ 해제
  const [unLockVisible, setUnlockVisible] = useState(false);
  const onUnLockCancle = () => {
    setUnlockVisible(false);
  };
  const onUnLockConfirm = () => {
    setUnlockVisible(false);
    onCostUnLock();
  };
  const onCostUnLock = async () => {
    try {
      const res = await chanceApi.unDoCost(project.prjUuid);

      if (res.data.info) {
        dispatch(setProject(res.data.info));
        // dispatch(listProjectCosts(project.prjUuid));
        // dispatch(projectEstimateActive(project.prjUuid));
        // dispatch(projectOrderCount(project.prjUuid));

        navigate('/chancemng');

        enqueueSnackbar('원가산출이 해제되었습니다.', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };
  const [initialValues, setInitialValues] = useState(getInitialFormValues());

  useEffect(() => {
    
    if (project) {
      const baseLayout = getInitialFormValues(); 
      const merged = _.mergeWith({}, baseLayout, project, (objValue, srcValue, key) => {

        if (key === 'invoiceToBe') {
          return _.mergeWith({}, baseLayout.invoiceToBe, srcValue || {}, (o, s) => s == null ? '' : undefined);
        }

        if (srcValue == null) return '';
        if (_.isDate(srcValue)) return srcValue;
        if (_.isArray(srcValue)) return [...srcValue];
      });

      setInitialValues(merged);
      setUpdateNot(project.invoiceToBe?.updateNot === 'N');
    }
  }, [project, dispatch]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationForecast(),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      //   console.log(values, 'values');
      //   formik.resetForm();
      // console.log(values, 'values');
      setSubmitting(false);
    },
  });

  const onSave = async (values) => {
    const isChangePrjTyp = project.prjTyp !== values.prjTyp ? true : false;
    
    const NewProjectData = {
      prjUuid: project?.prjUuid,
      expDay: dayjs(values.expDay).format('YYYY-MM-DD'),
      cstNm: values?.cstNm,
      prjTyp: values?.prjTyp,
      prjImpt: values?.prjImpt,
      bugetYn: values?.bugetYn,
      partnerTyp: values?.partnerTyp,
      clientTyp: values?.clientTyp,
      incClientTyp: values?.incClientTyp,
      procurement: values?.procurement,
      customerCf: values?.customerCf,
      salesTyp: values?.salesTyp,
      forcastTxt: values?.forcastTxt,
      productYn: values?.productYn,
      clientCmp: values?.clientCmp,
      clientCmpNm: values?.clientCmpNm,
      vendorCmp: values?.vendorCmp,
      vendorCmpNm: values?.vendorCmpNm,
      crtUsr: values?.crtUsr,
      ...(values.prjTyp === 'M' && {
        invoiceToBe: {
          prjUuid: project?.prjUuid,
          contractStDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractStDt).format('YYYY-MM-DD') : null,
          contractEdDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractEdDt).format('YYYY-MM-DD') : null,        
          inspectYn: values.invoiceToBe?.inspectYn,
          invTo: values.invoiceToBe?.invTo,
          invMonth : values.invoiceToBe?.invMonth,
          invDay: values.invoiceToBe?.invDay,
          colTo: values.invoiceToBe?.colTo,
          colDay: values.invoiceToBe?.colDay,
          colMonth : values.invoiceToBe?.colMonth,
          crtUsr: values?.crtUsr,
          lockYn: values.invoiceToBe?.lockYn,
          dateSchedules : values.invoiceToBe?.dateSchedules,
          updateNot : formik.values.invoiceToBe?.updateNot,
        },   
     })

    };

    try {
      const res = await chanceApi.editChance(NewProjectData);

      if (res.data) {
        dispatch(setProject(res.data.info));
        formik.setValues({
            ...res.data.info  // 서버에서 넘겨준 최신 객체로 덮어쓰기
        });
       enqueueSnackbar(`기본사항 저장 성공`, {
          variant: 'success',
        });
        setIsLocked(true);
        setUpdateNot(res.data.info.invoiceToBe?.updateNot === 'N');
        if (isChangePrjTyp) {
          const url =
            values.prjTyp === 'N'
              ? `/projectmng/${project?.prjUuid}/${project.cmpGrade}/1`
              : values.prjTyp === 'M' ? `/maintancetmng/${project?.prjUuid}/${project.cmpGrade}/1` : `/etcmng/${project?.prjUuid}/${project.cmpGrade}/1`;
          navigate(url);
        }
      }
    } catch (err) {
      let message =
        err.response !== 'undefined' ? err.response.data.message : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };
  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginLeft: '-17px',
        marginRight: '-17px',
      }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={() => setUnlockVisible(true)}
        >
          원가산출 해제
        </Button>
        <Button
          type="submit"
          variant="outlined"
          disabled={formik.isSubmitting || isLocked}
          onClick={async () => {
            // 1. 수동으로 유효성 검사 실행 (Yup 스키마 체크)
            const errors = await formik.validateForm();
            // 2. 에러가 존재하는지 확인
            const errorKeys = Object.keys(errors);
            if (errorKeys.length > 0) {
              // 3. 첫 번째 에러가 발생한 필드의 name으로 엘리먼트 찾기
              const firstErrorName = errorKeys[0];
              const element = document.getElementsByName(firstErrorName)[0];
              
              if (element) {
                // 4. 해당 필드로 부드럽게 스크롤 이동 및 포커스
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
              }
            }
          }}
          
        >
          저장
        </Button>
      </Box>
      
      <ForecastBaseInfoForm formik={formik} titleName={'기본사항'} isLocked={isLocked} handleLockClick={handleLockClick} hideLock={true} updateNot={updateNot}/>
      
      <DetailTemplate title="원가 산출 내역">
        <CostCalcLockContainer project={project} />
      </DetailTemplate>
      <DetailTemplate title="견적">
        {estimate ? (
          <>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적 번호
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteNo}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적일자
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteRegDt
                    ? estimate?.qteRegDt
                    : dayjs(estimate?.crtDt).format('YYYY-MM-DD')}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적 유효일자
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteValidDt}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자명
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate.qteUsrNm}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자 연락처
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteUsrTel}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자 이메일
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteUsrEml}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  총 견적 금액
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  ₩{' '}
                  {estimate?.qtePrice
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  유지보수기간
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.mntTor} 개월
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  납품 장소
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.destPlace}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  발행 조건
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteCondt}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  결제 조건
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.payCond}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 1
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc1}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 2
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc2}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 3
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc3}
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box>
            <Typography variant="subtitle2" component="div">
              현재 활성화된 견적서가 없습니다.
            </Typography>
          </Box>
        )}
      </DetailTemplate>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={() => setUnlockVisible(true)}
        >
          원가산출 해제
        </Button>
      </Box>
      
      <AskDialog
        visible={unLockVisible}
        title="프로젝트 원가 산출 해제"
        description={`원가산출을 해제하시겠습니까?\r\n작업된(견적서 / 수주품의서 ) 등의 사항을 다시 작성해야됩니다.`}
        confirmText="해제"
        onConfirm={onUnLockConfirm}
        onCancle={onUnLockCancle}
      />
    </Box>
  );
};

export default ProjectDetailInfoContainer;
